import type { ComponentPropsWithoutRef } from "react"
import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { colorClass } from "../../../utils/css"
import { Button, ButtonSize, Typography } from "../../common"
import { Overlay } from "../../common/atoms/Overlay"
import "./BroadcasterAgreementModal.scss"

type BroadcasterAgreementModalProps = ComponentPropsWithoutRef<"div"> & {
    onAgreement: () => void
}

export const mobileBroadcastTermsAgreed = "mobileBroadcastTermsAgreed"

export const hasBroadcasterAgreed = (): boolean => {
    return localStorage.getItem(mobileBroadcastTermsAgreed) === "true"
}

export function BroadcasterAgreementModal({
    onAgreement,
    ...props
}: BroadcasterAgreementModalProps) {
    const termsOfAgreement = [
        t`You are at least 18 years old or the age of majority in your jurisdiction, whichever is older (the "Age of Majority");`,
        t`Anyone appearing on camera with you has also reached the Age of Majority;`,
        t`At no time will anyone who has not reached the Age of Majority be present while you are using the Chaturbate service;`,
        t`You will not permit anyone who has not reached the Age of Majority to use the Chaturbate service; and`,
        <Trans key="emailSupportTerm">
            You will immediately report by email to{" "}
            <a href="mailto:support@chaturbate.com">support@chaturbate.com</a>{" "}
            any and all instances you become aware of wherein an individual has
            used the Chaturbate service without first reaching the Age of
            Majority.
        </Trans>,
        t`Obviously, you consent to and request your image being broadcast on the Chaturbate platform.`,
        t`You agree that you will be governed by the Chaturbate Terms and by the Chaturbate Broadcaster Agreement.`,
        t`You agree that only age verified broadcasters associated with this room may broadcast in this room.`,
    ]

    const agreeCallback = () => {
        localStorage.setItem(mobileBroadcastTermsAgreed, "true")
        onAgreement()
    }

    return (
        <Overlay
            className="broadcasterAgreementModal"
            data-testid="broadcaster-agreement-modal"
            {...props}
            onClick={() => {}}
        >
            <div className="broadcasterAgreement">
                <Typography
                    size={Sizes.mdpx}
                    weight={Weights.medium}
                    color={colorClass.brandBlue}
                    className="broadcasterAgreement__warning"
                >
                    <Trans>
                        WARNING: THIS SITE HAS A ZERO TOLERANCE POLICY ON
                        UNDERAGE BROADCASTING.
                    </Trans>
                </Typography>
                <div className="broadcasterAgreement__terms">
                    <Typography
                        size={Sizes.xs}
                        className="broadcasterAgreement__terms__text"
                    >
                        <Trans>
                            The Chaturbate service is for use by adults only. By
                            clicking the link below, you are affirming your
                            representation and warranty to us that:
                        </Trans>
                    </Typography>
                    <ol className="broadcasterAgreement__terms__list">
                        {termsOfAgreement.map((term, index) => (
                            <li key={index}>
                                <Typography
                                    size={Sizes.xs}
                                    className="broadcasterAgreement__terms__list__item"
                                >
                                    {term}
                                </Typography>
                            </li>
                        ))}
                    </ol>
                    <Typography
                        size={Sizes.xs}
                        className="broadcasterAgreement__terms__text"
                    >
                        <Trans>
                            If we suspect, in our sole discretion, that you are
                            using the Chaturbate service without having complied
                            in every respect with the foregoing statements, your
                            account will be terminated immediately, and we will
                            report your conduct to law enforcement.
                        </Trans>
                    </Typography>
                </div>
                <Button
                    className="broadcasterAgreement__button"
                    size={ButtonSize.Large}
                    text={t`I AGREE`}
                    onClick={agreeCallback}
                />
            </div>
        </Overlay>
    )
}
